// Worker utilisation cell.
//
// Leads with the worker's overall CPU% (normalized, 0-100) because that
// reads at-a-glance and works the same on a 4-core dev box and a 64-core
// fleet node. Loadavg lives in the tooltip — but framed as a
// `saturation %` (load / cores) instead of the bare Linux number, since
// "load 13" by itself looks alarming until you know the core count.
//
// Old snapshots that don't ship `cpu` fall back to the legacy loadavg
// readout (still with the saturation framing when we know the cores)
// so we don't regress when a worker hasn't been upgraded yet.

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";
import { cn } from "@veil/ui/lib/utils";
import type { RuntimeMetrics } from "@/lib/use-fleet-host-history";

interface Props {
	runtime: RuntimeMetrics | null | undefined;
}

function cpuTone(pct: number): string {
	if (pct < 50) return "bg-emerald-500";
	if (pct < 80) return "bg-amber-500";
	return "bg-destructive";
}

// Saturation = load / cores. <1.0 means the machine has headroom,
// =1.0 means it's matching its core count, >1.0 means threads are
// queueing for CPU. Matches the conventional SRE framing.
function saturationTone(sat: number | null): string {
	if (sat == null) return "text-muted-foreground";
	if (sat < 0.7) return "text-emerald-600";
	if (sat < 1.0) return "text-amber-600";
	return "text-destructive";
}

function formatSaturation(load: number, cores: number): string {
	const sat = load / cores;
	if (sat >= 1) return `${sat.toFixed(2)}× cores`;
	return `${Math.round(sat * 100)}% of cores`;
}

export function UtilCell({ runtime }: Props) {
	if (!runtime) {
		return <span className="text-xs text-muted-foreground">—</span>;
	}

	const cpu = runtime.cpu?.aggregate;
	const coreCount = runtime.cpu?.perCore?.length ?? 0;
	const load1 = runtime.loadAvg1m;
	const load5 = runtime.loadAvg5m;
	const load15 = runtime.loadAvg15m;
	const sat1 = coreCount > 0 ? load1 / coreCount : null;
	const sat5 = coreCount > 0 && load5 != null ? load5 / coreCount : null;
	const sat15 = coreCount > 0 && load15 != null ? load15 / coreCount : null;

	const tooltip = (
		<TooltipContent side="bottom" className="max-w-[18rem] text-[11px]">
			<div className="space-y-1.5">
				<div className="font-medium">Load average</div>
				<p className="text-muted leading-snug">
					Average number of threads runnable or waiting on disk. Not a
					percentage — divide by core count to read saturation.
				</p>
				<div className="grid grid-cols-3 gap-x-3 border-t border-background/20 pt-1.5 font-mono tabular-nums">
					<LoadCol label="1m" load={load1} sat={sat1} />
					<LoadCol label="5m" load={load5} sat={sat5} />
					<LoadCol label="15m" load={load15} sat={sat15} />
				</div>
				{coreCount > 0 ? (
					<div className="text-muted">
						{`On ${coreCount} cores · last minute ${formatSaturation(load1, coreCount)}`}
					</div>
				) : null}
			</div>
		</TooltipContent>
	);

	if (!cpu) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span
						className={cn(
							"inline-flex cursor-default items-baseline gap-1 font-mono text-xs tabular-nums",
							saturationTone(sat1),
						)}
					>
						{sat1 != null ? (
							<>
								<span className="font-semibold">{Math.round(sat1 * 100)}%</span>
								<span className="text-[10px] text-muted-foreground">
									of {coreCount} cores
								</span>
							</>
						) : (
							<>
								<span className="font-semibold">{load1.toFixed(2)}</span>
								<span className="text-[10px] text-muted-foreground">load</span>
							</>
						)}
					</span>
				</TooltipTrigger>
				{tooltip}
			</Tooltip>
		);
	}

	const pct = Math.min(100, Math.max(0, cpu.totalPct));

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="flex min-w-[88px] cursor-default flex-col gap-1">
					<div className="flex items-baseline justify-between gap-2 font-mono text-[11px] tabular-nums">
						<span className="font-semibold text-foreground">
							{pct.toFixed(0)}%
						</span>
						{sat1 != null && sat1 >= 0.7 ? (
							<span
								className={cn("text-[10px]", saturationTone(sat1))}
								title="Load average saturation (load / cores)"
							>
								{sat1 >= 1
									? `q ${sat1.toFixed(1)}×`
									: `q ${Math.round(sat1 * 100)}%`}
							</span>
						) : null}
					</div>
					<div
						className="relative h-1 w-full overflow-hidden rounded-full bg-muted/60"
						aria-hidden="true"
					>
						<div
							className={cn(
								"absolute inset-y-0 left-0 rounded-full",
								cpuTone(pct),
							)}
							style={{ width: `${pct}%` }}
						/>
					</div>
				</div>
			</TooltipTrigger>
			{tooltip}
		</Tooltip>
	);
}

function LoadCol({
	label,
	load,
	sat,
}: {
	label: string;
	load: number | null | undefined;
	sat: number | null;
}) {
	return (
		<div>
			<span className="block text-muted">{label}</span>
			<span className="block">{load != null ? load.toFixed(2) : "—"}</span>
			<span className={cn("block text-[10px]", saturationTone(sat))}>
				{sat != null
					? sat >= 1
						? `${sat.toFixed(2)}×`
						: `${Math.round(sat * 100)}%`
					: ""}
			</span>
		</div>
	);
}
